import {
  Checkbox,
  Collapse,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { SelectInputProps } from "@mui/material/Select/SelectInput";
import { ClassNameMap } from "@mui/styles";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import { ChangeEventHandler, ReactElement, useMemo } from "react";
import { AsyncButton } from "../../components/forms/AsyncButton";
import { ReasonTip } from "../../components/ReasonTip";
import { useCallbackSafeRef } from "../../hooks/useCallbackSafeRef";
import { DevConfigAction } from "../dev.types";

const useStyles = makeStyles(
  (theme) => ({
    root: {
      flexDirection: "column",
      alignItems: "stretch",
      background: theme.colors.white,
      borderRadius: theme.spacing(2),
      margin: theme.spacing(),
      width: "auto",
    },
    header: {
      cursor: "pointer",
    },
    collapseInner: {
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
    },
    formControl: {
      width: "100%",
    },
    arg: {
      gap: theme.spacing(),
      justifyContent: "start",
      minWidth: 200,
    },
    argInput: {
      width: "100%",
    },
    argInputNoStretch: {
      alignSelf: "start",
    },
    runWrapper: {
      alignSelf: "center",
    },
    run: {},
  }),
  {
    classNamePrefix: "DevActionListItem",
  }
);

export type DevActionListItemJSSClassKey = keyof ReturnType<typeof useStyles>;

export type DevActionListItemChangeHandler<K extends string, D extends Record<string, unknown>> = (
  key: K,
  data: D
) => unknown;

export type DevActionListitemRunHandler<K extends string> = (key: K) => unknown;
export type DevActionListitemOpenChangeHandler<K extends string> = (key: K, open: boolean) => unknown;

export type DevActionListItemProps<K extends string, D extends Record<string, unknown>> = {
  classes?: Partial<ClassNameMap<DevActionListItemJSSClassKey>>;
  className?: string;
  action: DevConfigAction<"client", K>;
  data: D;
  allowReload?: boolean;
  open?: boolean;
  onDataChange: DevActionListItemChangeHandler<K, D>;
  onRun: DevActionListitemRunHandler<K>;
  onOpenChange?: DevActionListitemOpenChangeHandler<K>;
};

export const DevActionListItem = <K extends string, D extends Record<string, unknown>>({
  className,
  classes: extClasses,
  action: { label, args, key, reloadAfter },
  data,
  allowReload,
  onDataChange,
  open,
  onOpenChange,
  onRun,
}: DevActionListItemProps<K, D>): ReactElement<DevActionListItemProps<K, D>> => {
  const classes = useStyles({
    classes: extClasses,
  });

  /********************/
  /*   custom hooks   */
  /********************/

  /********************/
  /*     useState     */
  /********************/

  /********************/
  /* useMemo & consts */
  /********************/

  const allArgsSet = useMemo(() => {
    if (!args) return true;
    return args.every((arg) => data[arg.key] !== undefined);
  }, [args, data]);

  /********************/
  /*    useCallback   */
  /********************/

  const toggleOpen = useCallbackSafeRef(() => onOpenChange?.(key, !open));
  const setDataKey = useCallbackSafeRef((argKey: string, value: unknown) =>
    onDataChange(key, {
      ...data,
      [argKey]: value,
    })
  );

  const run = useCallbackSafeRef(() => onRun(key));

  /********************/
  /*    useEffects    */
  /********************/

  /********************/
  /*       JSX        */
  /********************/

  return (
    <ListItem className={clsx(classes.root, className)}>
      <div className={classes.header} onClick={toggleOpen}>
        {label}
      </div>
      <Collapse in={open} classes={{ wrapperInner: classes.collapseInner }}>
        {args && (
          <List>
            {args.map(({ key, label, type }) => {
              const wrapInFormControlLabel = (control: ReactElement) => (
                <FormControlLabel className={classes.formControl} label={label} control={control} />
              );

              const wrapInFormControl = (control: ReactElement) => (
                <FormControl variant="standard" className={classes.formControl}>
                  <InputLabel>{label}</InputLabel>
                  {control}
                </FormControl>
              );

              return (
                <ListItem key={key} className={classes.arg}>
                  {(() => {
                    const value = data[key];
                    const handleSelectChange: NonNullable<SelectInputProps["onChange"]> = (e) =>
                      setDataKey(key, e.target.value);
                    const handleTextFieldChange: ChangeEventHandler<HTMLInputElement> = (e) =>
                      setDataKey(key, e.target.value);

                    switch (typeof type) {
                      case "object":
                        switch (type.specialType) {
                          case "select":
                            return wrapInFormControl(
                              <Select
                                variant="standard"
                                className={classes.argInput}
                                value={value || ""}
                                onChange={handleSelectChange}
                                placeholder="Select one..."
                              >
                                {type.values.map((argValue) => (
                                  <MenuItem key={argValue} value={argValue}>
                                    {argValue}
                                  </MenuItem>
                                ))}
                              </Select>
                            );
                          default:
                            return <>special type {type.specialType} not yet supported</>;
                        }
                      case "string":
                        switch (type) {
                          case "string":
                            return wrapInFormControl(
                              <TextField
                                variant="standard"
                                className={classes.argInput}
                                value={value}
                                onChange={handleTextFieldChange}
                                label={label}
                              />
                            );
                          case "integer":
                            return wrapInFormControl(
                              <TextField
                                variant="standard"
                                className={classes.argInput}
                                type="number"
                                value={value}
                                onChange={handleTextFieldChange}
                              />
                            );
                          case "boolean":
                            return wrapInFormControlLabel(
                              <Checkbox
                                className={classes.argInputNoStretch}
                                checked={value as boolean}
                                onChange={(e) => setDataKey(key, e.target.checked)}
                              />
                            );
                          default:
                            return <>type {type} not yet supported</>;
                        }
                      default:
                        return <>found an unexpected action of type {typeof type}</>;
                    }
                  })()}
                </ListItem>
              );
            })}
          </List>
        )}
        <ReasonTip
          className={classes.runWrapper}
          reasons={[{ test: !allArgsSet, message: "Please set all arguments" }]}
        >
          {(disabled) => (
            <AsyncButton className={classes.run} disabled={disabled} variant="contained" onClick={run} size="small">
              run
            </AsyncButton>
          )}
        </ReasonTip>
        {!!allowReload && !!reloadAfter && (
          <Typography align="center" variant="caption">
            (page will reload after)
          </Typography>
        )}
      </Collapse>
    </ListItem>
  );
};
